import { SortField, sortServicesBy } from 'components/pages/myServicesPage/layout/allservices/sort-service-list';
import { buildServiceBoardServiceKey, getServiceByKey, hasServiceKey } from 'store/ServiceBoardService/serviceBoardServiceThunk';
import { caseInsensitiveEquals } from 'core/utils/service-matchers';
import { AccessType } from 'models/accessType';
import { ServiceBoardCategoryModel, IServiceBoardServiceState } from 'models/serviceBoardService';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/store';
import { groupBy } from 'lodash-es';
import { useExternalService } from './useExternalService';
import { NonSgmService, Service } from 'services/ApiService/ServiceBoard/ServiceBoardApiClient';


const getServices = (serviceBoardServiceState: IServiceBoardServiceState, accessType: AccessType, language: string): [Service[], NonSgmService[], boolean] => {
    const serviceKey = buildServiceBoardServiceKey(accessType, language);
    const dataService = serviceBoardServiceState.data[serviceKey];
    return [dataService?.services || [], dataService?.nonSgmServices || [], dataService?.loading || false];
};

export const useHasAccessServices = () => {
    const serviceBoardServiceState = useAppSelector(state => state.boardServices);

    const hasAccessTo = (serviceKey: string): boolean => serviceBoardServiceState.accessServices.some((x) => caseInsensitiveEquals(x, serviceKey));
    const hasAccessToServicePage = (servicePageId: string): boolean => serviceBoardServiceState.accessPages.some((x) => caseInsensitiveEquals(x, servicePageId));

    return {
        hasAccessTo,
        hasAccessToServicePage,
    };
};

export const useBoardServices = (accessType: AccessType): {
    services: Service[], nonSgmServices: NonSgmService[], loading: boolean
} => {
    const { i18n: { language } } = useTranslation();
    const serviceBoardServiceState = useAppSelector(state => state.boardServices);
    const [services, nonSgmServices, loading] = getServices(serviceBoardServiceState, accessType, language);
    return {
        services,
        nonSgmServices,
        loading,
    };
};

export const useBoardServiceForDetail = (): {
    loading: boolean,
    getSgmServiceByKey: (key: string) => Service | null,
    hasSgmServiceKey: (key: Nullable<string>) => boolean
} => {
    const { services, loading } = useBoardServices(AccessType.All);

    return {
        loading,
        getSgmServiceByKey: (key) => getServiceByKey(services, key),
        hasSgmServiceKey: (key) => hasServiceKey(services, key),
    };
};

interface IUseCatalogServices {
    visibleServices: Service[],
    visibleCategories: ServiceBoardCategoryModel[],
    loading: boolean,
    getSgmServiceByKey: (key: string) => Nullable<Service>,
    getNonSgmServiceById: (id: string) => Nullable<NonSgmService>,
}
export const useCatalogServices = (): IUseCatalogServices => {
    const { services, nonSgmServices, loading } = useBoardServices(AccessType.BothForCatalog);
    const { showOnlyExternalService } = useExternalService();

    const visibleServices = services?.filter(x => !showOnlyExternalService || (!x.isInternal && x.sgmCatalog?.toLocaleLowerCase() === 'all')) || [];

    const servicesByCategory = groupBy(visibleServices, x => x.category?.key);
    const visibleCategories = Object.keys(servicesByCategory).filter(x => x).map(x => servicesByCategory[x][0].category as ServiceBoardCategoryModel);

    return {
        visibleServices: sortServicesBy(visibleServices, SortField.Title),
        visibleCategories,
        loading,
        getSgmServiceByKey: (key) => getServiceByKey(services, key),
        getNonSgmServiceById: (id) => nonSgmServices?.find(nss => nss.id === id),
    };
};
